import {
  AfterDateValueConfigKind,
  BeforeDateValueConfigKind,
  BetweenDateValueConfigKind,
  CohortFilterGranularity,
  InTheLastValueConfigKind,
  LastInteractionTagFilterConfig,
} from '@/api/generated/model';
import { FILTERS } from '@/models/common/filters';
import { CohortUserFilter, isCohortUserFilter } from './cohort-user-filters';
import type { CohortNumericComparatorKind } from './cohort-filter-controls';

function isPositiveNumber(value: unknown): value is number {
  return typeof value === 'number' && !Number.isNaN(value) && value > 0;
}

function isNonIntegerNumericValue(value: unknown): boolean {
  return typeof value === 'number' && !Number.isNaN(value) && !Number.isInteger(value);
}

export function isValidInTheLastValue(value: unknown): boolean {
  return isPositiveNumber(value) && Number.isInteger(value);
}

export function isInvalidNumericValue(value: number | null | undefined): boolean {
  return !isValidInTheLastValue(value);
}

function getGranularityPluralLabel(granularity: CohortFilterGranularity | undefined): string {
  switch (granularity) {
    case CohortFilterGranularity.day:
      return 'days';
    case CohortFilterGranularity.month:
      return 'months';
    case CohortFilterGranularity.week:
    default:
      return 'weeks';
  }
}

function getInTheLastConfigGranularity(config: unknown): CohortFilterGranularity | undefined {
  if (!config || typeof config !== 'object' || !('granularity' in config)) return undefined;
  return (config as { granularity?: CohortFilterGranularity }).granularity;
}

function isValidInTheLastConfig(config: unknown): boolean {
  if (!config || typeof config !== 'object' || !('kind' in config)) return true;
  if ((config as { kind: string }).kind !== InTheLastValueConfigKind.in_the_last) return true;
  return isValidInTheLastValue((config as { value?: number }).value);
}

function isValidSpecialTimeValues(values: unknown): boolean {
  if (!values || typeof values !== 'object' || !('kind' in values)) return false;

  const config = values as { kind: string; value?: unknown };
  if (config.kind === InTheLastValueConfigKind.in_the_last) {
    return isValidInTheLastValue((config as { value?: number }).value);
  }

  if (config.kind === AfterDateValueConfigKind.after || config.kind === BeforeDateValueConfigKind.before) {
    return config.value != null && config.value !== '';
  }

  if (config.kind === BetweenDateValueConfigKind.between) {
    const range = config.value as { start?: string; end?: string } | undefined;
    return Boolean(range?.start && range?.end);
  }

  return false;
}

function isValidLastInteractionTagFilter(filter: LastInteractionTagFilterConfig): boolean {
  if (!filter.tag) return false;
  if (filter.select_all) return true;
  if (filter.search?.trim()) return true;
  return (filter.values?.length ?? 0) > 0;
}

type CountConfigLike = {
  kind?: string;
  value?: number | number[];
};

export function isInvalidBetweenOrder(first: number, second: number): boolean {
  return first >= second;
}

export function isActivePeriodsValueOutOfBounds(
  kind: CohortNumericComparatorKind | string,
  value: number | [number, number],
  window: number,
): boolean {
  if (!isPositiveNumber(window)) return false;

  if (kind === 'between' && Array.isArray(value)) {
    return value[0] > window || value[1] > window;
  }

  if (typeof value !== 'number') return false;

  if (kind === 'above') return value >= window;
  if (kind === 'exactly' || kind === 'below') return value > window;
  return false;
}

function hasInvalidCountConfigPositivity(count: CountConfigLike | undefined | null): boolean {
  if (!count) return true;
  if (count.kind === 'between') {
    if (!Array.isArray(count.value) || count.value.length < 2) return true;
    return !isPositiveNumber(count.value[0]) || !isPositiveNumber(count.value[1]);
  }
  return !isPositiveNumber(count.value);
}

function hasInvalidCountConfigInteger(count: CountConfigLike | undefined | null): boolean {
  if (!count) return false;
  if (count.kind === 'between') {
    if (!Array.isArray(count.value) || count.value.length < 2) return false;
    return isNonIntegerNumericValue(count.value[0]) || isNonIntegerNumericValue(count.value[1]);
  }
  return isNonIntegerNumericValue(count.value);
}

function hasInvalidBetweenOrderInCount(count: CountConfigLike | undefined | null): boolean {
  if (!count || count.kind !== 'between') return false;
  if (!Array.isArray(count.value) || count.value.length < 2) return false;
  if (!isPositiveNumber(count.value[0]) || !isPositiveNumber(count.value[1])) return false;
  return isInvalidBetweenOrder(count.value[0], count.value[1]);
}

function isValidBetweenOrder(count: CountConfigLike | undefined | null): boolean {
  return !hasInvalidBetweenOrderInCount(count);
}

function isValidActivePeriodsBounds(activeCount: CountConfigLike, window: number): boolean {
  if (!isPositiveNumber(window)) return false;

  const kind = (activeCount.kind ?? 'above') as CohortNumericComparatorKind;

  if (kind === 'between' && Array.isArray(activeCount.value)) {
    const [first, second] = activeCount.value;
    if (!isPositiveNumber(first) || !isPositiveNumber(second)) return false;
    if (isInvalidBetweenOrder(first, second)) return false;
    return !isActivePeriodsValueOutOfBounds(kind, [first, second], window);
  }

  if (!isPositiveNumber(activeCount.value)) return false;
  return !isActivePeriodsValueOutOfBounds(kind, activeCount.value as number, window);
}

function isValidCountConfig(count: CountConfigLike | undefined | null): boolean {
  if (hasInvalidCountConfigPositivity(count)) return false;
  if (hasInvalidCountConfigInteger(count)) return false;
  return isValidBetweenOrder(count);
}

function getCountConfigFromFilter(filter: CohortUserFilter): CountConfigLike | null {
  switch (filter.column) {
    case FILTERS.usersInteraction:
      return filter.values.interactions_count;
    case FILTERS.userConversationsCount:
      return filter.values.conversations_count;
    case FILTERS.userConversationLength:
      return filter.values.interactions_count;
    case FILTERS.activePeriods:
      return filter.values.active_count;
    case FILTERS.avgSessionLength:
      return filter.values.duration;
    default:
      return null;
  }
}

export function isValidCohortUserFilter(filter: unknown): filter is CohortUserFilter {
  if (!isCohortUserFilter(filter)) return false;

  switch (filter.column) {
    case FILTERS.lastInteractionTag:
      return isValidLastInteractionTagFilter(filter);

    case FILTERS.firstInteraction:
    case FILTERS.lastInteraction:
      return isValidSpecialTimeValues(filter.values);

    case FILTERS.usersInteraction: {
      const { interactions_count: count, dates } = filter.values;
      if (!isValidCountConfig(count)) return false;
      if (dates) {
        return isValidInTheLastConfig(dates);
      }
      return true;
    }

    case FILTERS.userConversationsCount: {
      const { conversations_count: count, dates } = filter.values;
      if (!isValidCountConfig(count)) return false;
      if (dates) {
        return isValidInTheLastConfig(dates);
      }
      return true;
    }

    case FILTERS.userConversationLength: {
      const { interactions_count: count, dates } = filter.values;
      if (!isValidCountConfig(count)) return false;
      if (dates) {
        return isValidInTheLastConfig(dates);
      }
      return true;
    }

    case FILTERS.activePeriods: {
      const { active_count: activeCount, window } = filter.values;
      if (!isValidCountConfig(activeCount)) return false;
      return isValidActivePeriodsBounds(activeCount, window);
    }

    case FILTERS.avgSessionLength: {
      const { duration, dates } = filter.values;
      if (!isValidCountConfig(duration)) return false;
      if (dates) {
        return isValidInTheLastConfig(dates);
      }
      return true;
    }

    default:
      return false;
  }
}

function hasInvalidRollingWindowPositivity(filter: CohortUserFilter): boolean {
  switch (filter.column) {
    case FILTERS.firstInteraction:
    case FILTERS.lastInteraction:
      if (filter.values?.kind === InTheLastValueConfigKind.in_the_last) {
        return !isPositiveNumber(filter.values.value);
      }
      return false;

    case FILTERS.activePeriods:
      return !isPositiveNumber(filter.values.window);

    case FILTERS.usersInteraction:
      if (!filter.values.dates || filter.values.dates.kind !== InTheLastValueConfigKind.in_the_last) return false;
      return !isPositiveNumber(filter.values.dates.value);

    case FILTERS.avgSessionLength:
      if (!filter.values.dates || filter.values.dates.kind !== InTheLastValueConfigKind.in_the_last) return false;
      return !isPositiveNumber(filter.values.dates.value);

    case FILTERS.userConversationsCount:
      if (!filter.values.dates || filter.values.dates.kind !== InTheLastValueConfigKind.in_the_last) return false;
      return !isPositiveNumber(filter.values.dates.value);

    case FILTERS.userConversationLength:
      if (!filter.values.dates || filter.values.dates.kind !== InTheLastValueConfigKind.in_the_last) return false;
      return !isPositiveNumber(filter.values.dates.value);

    default:
      return false;
  }
}

function hasInvalidRollingWindowInteger(filter: CohortUserFilter): boolean {
  switch (filter.column) {
    case FILTERS.firstInteraction:
    case FILTERS.lastInteraction:
      if (filter.values?.kind === InTheLastValueConfigKind.in_the_last) {
        return isNonIntegerNumericValue(filter.values.value);
      }
      return false;

    case FILTERS.activePeriods:
      return isNonIntegerNumericValue(filter.values.window);

    case FILTERS.usersInteraction:
      if (!filter.values.dates || filter.values.dates.kind !== InTheLastValueConfigKind.in_the_last) return false;
      return isNonIntegerNumericValue(filter.values.dates.value);

    case FILTERS.avgSessionLength:
      if (!filter.values.dates || filter.values.dates.kind !== InTheLastValueConfigKind.in_the_last) return false;
      return isNonIntegerNumericValue(filter.values.dates.value);

    case FILTERS.userConversationsCount:
      if (!filter.values.dates || filter.values.dates.kind !== InTheLastValueConfigKind.in_the_last) return false;
      return isNonIntegerNumericValue(filter.values.dates.value);

    case FILTERS.userConversationLength:
      if (!filter.values.dates || filter.values.dates.kind !== InTheLastValueConfigKind.in_the_last) return false;
      return isNonIntegerNumericValue(filter.values.dates.value);

    default:
      return false;
  }
}

function hasInvalidCountPositivity(filter: CohortUserFilter): boolean {
  const count = getCountConfigFromFilter(filter);
  if (!count) return false;
  return hasInvalidCountConfigPositivity(count);
}

function hasInvalidCountInteger(filter: CohortUserFilter): boolean {
  const count = getCountConfigFromFilter(filter);
  if (!count) return false;
  return hasInvalidCountConfigInteger(count);
}

function hasInvalidBetweenOrder(filter: CohortUserFilter): boolean {
  const count = getCountConfigFromFilter(filter);
  if (!count) return false;
  return hasInvalidBetweenOrderInCount(count);
}

function hasInvalidActivePeriodsBounds(filter: CohortUserFilter): boolean {
  if (filter.column !== FILTERS.activePeriods) return false;
  const { active_count: activeCount, window } = filter.values;
  if (hasInvalidCountConfigPositivity(activeCount) || !isPositiveNumber(window)) return false;
  return !isValidActivePeriodsBounds(activeCount, window);
}

function getCountFieldLabel(filter: CohortUserFilter): string {
  switch (filter.column) {
    case FILTERS.usersInteraction:
      return 'interactions';
    case FILTERS.userConversationsCount:
      return 'conversations';
    case FILTERS.userConversationLength:
      return 'turns';
    case FILTERS.activePeriods:
      return 'active periods';
    case FILTERS.avgSessionLength:
      return 'duration';
    default:
      return 'value';
  }
}

function getRollingWindowValidationError(filter: CohortUserFilter): string | null {
  if (!hasInvalidRollingWindowPositivity(filter)) return null;

  switch (filter.column) {
    case FILTERS.activePeriods: {
      const unit = getGranularityPluralLabel(filter.values.granularity);
      return `Number of ${unit} in "of the last" must be greater than 0.`;
    }

    case FILTERS.firstInteraction:
    case FILTERS.lastInteraction: {
      const unit = getGranularityPluralLabel(getInTheLastConfigGranularity(filter.values));
      return `Number of ${unit} in "in the last" must be greater than 0.`;
    }

    case FILTERS.usersInteraction: {
      const unit = getGranularityPluralLabel(getInTheLastConfigGranularity(filter.values.dates));
      return `Number of ${unit} in "over the last" must be greater than 0.`;
    }

    case FILTERS.avgSessionLength: {
      const unit = getGranularityPluralLabel(getInTheLastConfigGranularity(filter.values.dates));
      return `Number of ${unit} in "over the last" must be greater than 0.`;
    }

    case FILTERS.userConversationsCount:
    case FILTERS.userConversationLength: {
      const windowUnit = getGranularityPluralLabel(getInTheLastConfigGranularity(filter.values.dates));
      return `Number of ${windowUnit} in "over the last" must be greater than 0.`;
    }

    default:
      return 'Time window must be greater than 0.';
  }
}

function getRollingWindowIntegerValidationError(filter: CohortUserFilter): string | null {
  if (!hasInvalidRollingWindowInteger(filter)) return null;

  switch (filter.column) {
    case FILTERS.activePeriods: {
      const unit = getGranularityPluralLabel(filter.values.granularity);
      return `Number of ${unit} in "of the last" must be a whole number.`;
    }

    case FILTERS.firstInteraction:
    case FILTERS.lastInteraction: {
      const unit = getGranularityPluralLabel(getInTheLastConfigGranularity(filter.values));
      return `Number of ${unit} in "in the last" must be a whole number.`;
    }

    case FILTERS.usersInteraction: {
      const unit = getGranularityPluralLabel(getInTheLastConfigGranularity(filter.values.dates));
      return `Number of ${unit} in "over the last" must be a whole number.`;
    }

    case FILTERS.avgSessionLength: {
      const unit = getGranularityPluralLabel(getInTheLastConfigGranularity(filter.values.dates));
      return `Number of ${unit} in "over the last" must be a whole number.`;
    }

    case FILTERS.userConversationsCount:
    case FILTERS.userConversationLength: {
      const windowUnit = getGranularityPluralLabel(getInTheLastConfigGranularity(filter.values.dates));
      return `Number of ${windowUnit} in "over the last" must be a whole number.`;
    }

    default:
      return 'Time window must be a whole number.';
  }
}

function getCountValidationError(filter: CohortUserFilter): string | null {
  if (!hasInvalidCountPositivity(filter)) return null;

  const label = getCountFieldLabel(filter);
  const count = getCountConfigFromFilter(filter);

  if (count?.kind === 'between' && Array.isArray(count.value)) {
    return `Both ${label} values must be greater than 0.`;
  }
  if (filter.column === FILTERS.activePeriods) {
    return 'Number of active periods must be greater than 0.';
  }
  if (filter.column === FILTERS.avgSessionLength) {
    return 'Duration must be greater than 0.';
  }
  return `Number of ${label} must be greater than 0.`;
}

function getCountIntegerValidationError(filter: CohortUserFilter): string | null {
  if (!hasInvalidCountInteger(filter)) return null;

  const label = getCountFieldLabel(filter);
  const count = getCountConfigFromFilter(filter);

  if (count?.kind === 'between' && Array.isArray(count.value)) {
    return `Both ${label} values must be whole numbers.`;
  }
  if (filter.column === FILTERS.activePeriods) {
    return 'Number of active periods must be a whole number.';
  }
  if (filter.column === FILTERS.avgSessionLength) {
    return 'Duration must be a whole number.';
  }
  return `Number of ${label} must be a whole number.`;
}

function getBetweenOrderValidationError(filter: CohortUserFilter): string | null {
  if (!hasInvalidBetweenOrder(filter)) return null;
  return 'The first value must be lower than the second value.';
}

function getActivePeriodsComparatorLabel(kind: string | undefined): string {
  switch (kind) {
    case 'above':
      return 'more than';
    case 'below':
      return 'fewer than';
    case 'exactly':
      return 'exactly';
    case 'between':
      return 'between';
    default:
      return kind ?? 'more than';
  }
}

function getActivePeriodsBoundsValidationError(filter: CohortUserFilter): string | null {
  if (!hasInvalidActivePeriodsBounds(filter)) return null;
  if (filter.column !== FILTERS.activePeriods) return null;

  const unit = getGranularityPluralLabel(filter.values.granularity);
  const count = filter.values.active_count;
  const kind = count.kind ?? 'above';

  if (kind === 'above') {
    return `Active periods in "more than" must be fewer than the number of ${unit} in "of the last".`;
  }

  if (kind === 'between') {
    return `Active period values cannot exceed the number of ${unit} in "of the last".`;
  }

  const opLabel = getActivePeriodsComparatorLabel(kind);
  return `Active periods in "${opLabel}" cannot exceed the number of ${unit} in "of the last".`;
}

export function getCohortUserFilterValidationError(filter: CohortUserFilter): string | null {
  if (isValidCohortUserFilter(filter)) return null;
  return (
    getRollingWindowValidationError(filter) ??
    getRollingWindowIntegerValidationError(filter) ??
    getCountValidationError(filter) ??
    getCountIntegerValidationError(filter) ??
    getBetweenOrderValidationError(filter) ??
    getActivePeriodsBoundsValidationError(filter)
  );
}
